import {
    Col,
    Row,
    Space,
    Typography,
} from 'antd';
import {
    FC,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { centerByCountry } from '../../utils/countries.utils';
import { SearchLocation } from '../SearchLocation';
import { useWorkspaceStore } from '../../store/workspace.store';
import { DriversMap } from '../DriversMap';
import { useDriversContext } from './context/drivers.ctx';
import { DriverMarker } from '../DriverMarker';
import { ICoordinates } from '../../interfaces/map.interface';
import { IDriver } from '../../interfaces/driver.interface';

const { Text } = Typography;

const DEFAULT_ZOOM = 12;

const isValidCoord = (lat: number, lng: number) => lat !== 0 && lng !== 0
    && lat >= -60 && lat <= 35 && lng >= -120 && lng <= -30;

const getDriversBounds = (drivers: IDriver[]): [number, number, number, number] | null => {
    const located = drivers.filter((d) => {
        const lat = d.currentDriverLocation?.coordinates.lat;
        const lng = d.currentDriverLocation?.coordinates.lng;
        return lat != null && lng != null && isValidCoord(lat, lng);
    });

    if (!located.length) return null;

    const lats = located.map((d) => d.currentDriverLocation!.coordinates.lat);
    const lngs = located.map((d) => d.currentDriverLocation!.coordinates.lng);

    return [
        Math.min(...lngs), // west
        Math.min(...lats), // south
        Math.max(...lngs), // east
        Math.max(...lats), // north
    ];
};

const CommandCenter: FC = () => {
    const { t } = useTranslation('drivers', { keyPrefix: 'DRIVERS' });

    const workspace = useWorkspaceStore((state) => state.data);

    const { drivers } = useDriversContext();

    const countryCenter = centerByCountry(workspace?.country as string);
    const [center, setCenter] = useState<ICoordinates>(countryCenter);
    const [zoom, setZoom] = useState<number>(DEFAULT_ZOOM);
    const [setMapsLoaded] = useState<any>();
    const [bounds, setBounds] = useState<[number, number, number, number] | null>(null);

    const driversBounds = useMemo(() => getDriversBounds(drivers || []), [drivers]);

    useEffect(() => {
        if (driversBounds && !bounds) {
            setBounds(driversBounds);
        }
    }, [driversBounds]);

    const handleUpdateLocation = ({ center, zoom }: { center: ICoordinates; zoom: number }) => {
        setBounds(null);
        setCenter(center);
        setZoom(zoom);
    };

    const setDefaultLocation = () => {
        if (driversBounds) {
            setBounds([...driversBounds]);
        } else {
            setCenter(countryCenter);
            setZoom(DEFAULT_ZOOM);
        }
    };

    return (
        <div className="section">
            <Space direction="vertical" size={24} style={{ width: '100%' }}>
                <div className="help">
                    <Text>
                        <Trans
                            t={t}
                            i18nKey="COMMAND_CENTER_HELP"
                            components={{
                                b: <b />,
                            }}
                        />
                    </Text>
                </div>

                <Row justify="start" align="middle" gutter={16}>
                    <Col flex="1">
                        <SearchLocation
                            updateLocation={handleUpdateLocation}
                            setDefaultLocation={setDefaultLocation}
                        />
                    </Col>
                </Row>
                <div style={{ height: 650 }}>
                    <DriversMap
                        setMapsLoaded={setMapsLoaded}
                        center={center}
                        zoom={zoom}
                        bounds={bounds}
                    >
                        {
                            drivers && drivers
                                .filter((d: IDriver) => {
                                    const lat = d.currentDriverLocation?.coordinates.lat;
                                    const lng = d.currentDriverLocation?.coordinates.lng;
                                    return lat != null && lng != null && isValidCoord(lat, lng);
                                })
                                .map((driver: IDriver) => (
                                    <DriverMarker
                                        key={driver._id}
                                        driverID={driver._id}
                                        driverUniqueID={driver.userUniqueID}
                                        name={driver.name}
                                        lastName={driver.lastName}
                                        email={driver.email}
                                        mobile={driver.fullMobile}
                                        isBusy={driver.isBusy}
                                        onDutyFlag={driver.onDutyFlag}
                                        lat={driver.currentDriverLocation!.coordinates.lat}
                                        lng={driver.currentDriverLocation!.coordinates.lng}
                                        driverMarkerSelected=""
                                        updatedDriverMarkerSelected={() => { }}
                                    />
                                ))
                        }
                    </DriversMap>
                </div>
            </Space>
        </div>
    );
};

export { CommandCenter };
